<template>
  <div class="data-acquisition-container">
    <!-- 头部标题与过滤器 -->
    <div class="header-section">
      <div class="title-container">
        <h2 class="main-title">设备数据采集</h2>
        <p class="sub-title">请选择要查看的设备，实时监控运行状态与能效数据</p>
      </div>

      <!-- 右侧筛选过滤 -->
      <div class="filter-container">
        <el-input 
          v-model="searchQuery" 
          placeholder="搜索设备名称..." 
          class="search-input"
          clearable
          @keyup.enter="handleQuery"
        />
        <el-select v-model="statusQuery" placeholder="全部状态" class="status-select">
          <el-option label="全部状态" value="all" />
          <el-option label="在线" value="1" />
          <el-option label="离线" value="0" />
          <el-option label="故障" value="2" />
        </el-select>
        <el-button type="primary" class="btn-query" @click="handleQuery">查询</el-button>
        <el-button class="btn-reset" @click="handleReset">重置</el-button>
      </div>
    </div>

    <!-- 设备卡片网格 -->
    <div class="grid-container" v-loading="loading">
      <div class="device-grid" v-if="filteredTableData.length > 0">
        <div class="device-card" v-for="(item, index) in filteredTableData" :key="index" @click="goToScreen(item)">
          <!-- 卡片头部：图标与状态 -->
          <div class="card-header">
            <!-- v-html="getDeviceIcon(item.device_type)" -->
            <!-- <span class="device-icon-wrapper" ></span> -->
              <h4 class="device-name">{{ item.device_name }}</h4>
            <span :class="['status-dot-text', getStatusClass(item.status)]">
              ● {{ getStatusText(item.status) }}  
            </span>
          </div>
         
          <!-- 卡片中部：名称与型号 -->
          <div class="card-body">
           
            <div class="device-subtitle">{{ item.device_sn }} · {{ item.device_type_name }}</div>
          </div>

          <!-- 卡片尾部：元数据表格 -->
          <div class="card-footer">
            <div class="meta-row">
              <span class="meta-label">所属项目</span>
              <span class="meta-val">{{ item.tenant_name || '--' }}</span>
            </div>
            <div class="meta-row">
              <span class="meta-label">设备位置</span>
              <span class="meta-val">{{ item.hardware_model || '--' }}</span>
            </div>
            <div class="meta-row">
              <span class="meta-label">最后在线</span>
              <span class="meta-val">{{ formatOnlineTime(item.manufacture_date) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 空数据状态 -->
      <div class="empty-state" v-else>
        <el-empty description="暂无设备数采数据" />
      </div>
    </div>

    <!-- 底部分页器 -->
    <div class="pagination-container">
      <el-config-provider :locale="zhCn">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="limit"
          :page-sizes="[12, 20, 40, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount"
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </el-config-provider>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { ElConfigProvider } from "element-plus";
import zhCn from "element-plus/es/locale/lang/zh-cn";
import { useRouter } from "vue-router";
import { EquipmentLedgerList } from '@/api/equipmentLedger.js';

const router = useRouter();
const searchQuery = ref("");
const statusQuery = ref("all");
const page = ref(1);
const limit = ref(20);
const totalCount = ref(0);
const loading = ref(false);
const tableData = ref([]);

const goToScreen = (item) => {
  router.push({
    path: '/equipment/deviceScreen',
    query: {
      id: item.id,
      name: item.device_name,
      sn: item.device_sn,
      type: item.device_type,
      tenant: item.tenant_name,
      model: item.hardware_model,
      date: item.manufacture_date,
      status: item.status
    }
  });
};

// 获取列表数据
const getList = async () => {
  loading.value = true;
  try {
    const res = await EquipmentLedgerList({
      current: page.value,
      size: limit.value,
      deviceName: searchQuery.value
    });
    if (res.code === 200 && res.data) {
      tableData.value = res.data.records || [];
      totalCount.value = res.data.total || 0;
    } else {
      tableData.value = [];
      totalCount.value = 0;
    }
  } catch (error) {
    console.error("加载数采数据失败:", error);
  } finally {
    loading.value = false;
  }
};

// 状态过滤 (客户端混合过滤以适配全部状态/在线/离线/故障)
const filteredTableData = computed(() => {
  if (statusQuery.value === 'all') {
    return tableData.value;
  }
  return tableData.value.filter(item => {
    // 假设 status: 1 = 在线, 0 = 离线, 2 = 故障
    return String(item.status) === String(statusQuery.value);
  });
});

// 查询与重置
const handleQuery = () => {
  page.value = 1;
  getList();
};

const handleReset = () => {
  searchQuery.value = "";
  statusQuery.value = "all";
  page.value = 1;
  getList();
};

// 分页变更
const handleSizeChange = (newSize) => {
  limit.value = newSize;
  page.value = 1;
  getList();
};

const handleCurrentChange = (newPage) => {
  page.value = newPage;
  getList();
};

// 工具方法：映射状态样式
const getStatusClass = (status) => {
  if (status === 1 || status === '1') return 'status-online';
  if (status === 0 || status === '0') return 'status-offline';
  return 'status-faulty';
};

// 工具方法：映射状态文字
const getStatusText = (status) => {
  if (status === 1 || status === '1') return '在线';
  if (status === 0 || status === '0') return '离线';
  return '故障';
};

// 工具方法：格式化最后在线时间
const formatOnlineTime = (dateStr) => {
  if (!dateStr) return '10:30';
  // 如果是标准日期格式 YYYY-MM-DD，返回时间缩写或直接返回，若没有时间则模拟一个
  if (dateStr.includes('-')) {
    return dateStr.slice(5) + ' 10:20';
  }
  return dateStr;
};

// 根据设备类型返回高科技感 SVG 图标路径
const getDeviceIcon = (type) => {
  const t = type || '';
  if (t.includes('整平')) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 10h16M4 14h16M2 12h20" stroke-linecap="round"/><circle cx="6" cy="12" r="2" fill="currentColor"/><circle cx="18" cy="12" r="2" fill="currentColor"/></svg>`;
  } else if (t.includes('抹光')) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg>`;
  } else if (t.includes('混凝土') || t.includes('泵车')) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="12" rx="2"/><path d="M7 21h10M12 15v6M3 9h18"/></svg>`;
  } else if (t.includes('塔吊') || t.includes('起重')) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M4 5h16M8 5l4-3 4 3M6 22h12"/></svg>`;
  } else if (t.includes('无人机') || t.includes('飞行')) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0M3 12h6M15 12h6M12 3v6M12 15v6"/></svg>`;
  }
  // 默认齿轮图标
  return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;
};

onMounted(() => {
  getList();
});
</script>

<style lang="less" scoped>
.data-acquisition-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #f4f5fa;
  padding: 20px;
  box-sizing: border-box;
  // border: 1px solid red;
}

/* 头部排版 */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background-color: #fff;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.title-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.main-title {
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
  margin: 0;
}

.sub-title {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

/* 右侧筛选面板 */
.filter-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  width: 200px;
}

.status-select {
  width: 130px;
}

.btn-query {
  background-color: #1e6aed;
  border: none;
  font-size: 13px;
  font-weight: 500;
  &:hover {
    background-color: #1557c0;
  }
}

.btn-reset {
  font-size: 13px;
  color: #4b5563;
  border: 1px solid #d1d5db;
  &:hover {
    background-color: #f9fafb;
  }
}

/* 网格区 */
.grid-container {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
  min-height: 0;
}

.device-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.device-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(229, 231, 235, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
    border-color: #1e6aed;
  }
}

/* 卡片头部 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.device-icon-wrapper {
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-dot-text {
  font-size: 12px;
  font-weight: 600;
  margin-left: auto;
  
  &.status-online {
    color: #22c55e;
  }
  &.status-offline {
    color: #f97316;
  }
  &.status-faulty {
    color: #ef4444;
  }
}

/* 卡片中部 */
.card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device-name {
  font-size: 16px;
  font-weight: bold;
  color: #111827;
  margin: 0;
}

.device-subtitle {
  font-size: 12px;
  color: #9ca3af;
}

/* 卡片底部元数据 */
.card-footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.meta-label {
  color: #9ca3af;
}

.meta-val {
  color: #374151;
  font-weight: 500;
  max-width: 140px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

/* 分页器 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  background-color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #fff;
  border-radius: 8px;
}
</style>
